import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ShieldOff, CheckCircle2, RefreshCw, X } from "lucide-react";

/**
 * Phase 49d — Upstox connectivity banner.
 * Polls /api/upstox/connectivity-status every 30s (foreground) and renders a
 * persistent banner near the Live Trades panel when Upstox is degraded or
 * down. Silent when everything is healthy so it never adds noise.
 *
 * `status`:
 *   - "ok"        → do not render anything
 *   - "degraded"  → amber banner listing the active warnings
 *   - "down"      → red banner (typically token missing/expired)
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .format(new Date(s))
      .replace(",", "");
  } catch {
    return "—";
  }
};

const SEVERITY_STYLE = {
  error: {
    color: "var(--bear)",
    bg: "rgba(228, 90, 90, 0.08)",
    border: "var(--bear)",
    icon: ShieldOff,
    label: "Upstox down",
  },
  warn: {
    color: "#d97706",
    bg: "rgba(217, 119, 6, 0.08)",
    border: "#d97706",
    icon: AlertTriangle,
    label: "Upstox degraded",
  },
  info: {
    color: "var(--bull)",
    bg: "rgba(96, 200, 130, 0.08)",
    border: "var(--bull)",
    icon: CheckCircle2,
    label: "Upstox connected",
  },
};

export const UpstoxConnectivityBanner = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/upstox/connectivity-status?limit=5`);
      setData(r.data);
      // When Upstox recovers, un-dismiss so the next issue shows again.
      if (r.data?.status === "ok") setDismissed(false);
    } catch {
      /* best-effort — silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const status = data?.status || "ok";
  const active = data?.active || [];

  if (status === "ok" || active.length === 0 || dismissed) return null;

  const top = active[0];
  const severity = top?.severity || (status === "down" ? "error" : "warn");
  const style = SEVERITY_STYLE[severity] || SEVERITY_STYLE.warn;
  const Icon = style.icon;

  return (
    <div
      data-testid="upstox-connectivity-banner"
      className="border-l-4 border border-grid px-4 py-3 flex items-start gap-3"
      style={{
        borderLeftColor: style.border,
        background: style.bg,
        color: "var(--text-primary)",
      }}
    >
      <div className="pt-0.5" style={{ color: style.color }}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <strong
            className="text-[11px] uppercase tracking-[0.18em] font-mono-data"
            style={{ color: style.color }}
            data-testid="upstox-connectivity-banner-label"
          >
            {style.label} · {top.label}
          </strong>
          {top.section && (
            <span
              className="inline-flex items-center text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 border"
              style={{ color: style.color, borderColor: style.color }}
            >
              {top.section}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            {fmtIST(top.created_at)}
          </span>
        </div>
        {top.detail && (
          <div className="text-xs text-muted-1" data-testid="upstox-connectivity-banner-detail">
            {top.detail}
          </div>
        )}
        {active.length > 1 && (
          <div className="text-[10px] text-muted-3 uppercase tracking-wider font-mono-data">
            + {active.length - 1} other issue{active.length > 2 ? "s" : ""} in the last 15 min
          </div>
        )}
        <div className="text-[10px] text-muted-3">
          Fix suggestions: reconnect Upstox from Profile → Upstox, verify the access token
          hasn't expired, or wait for the network blip to clear. Live-trading orders that
          rely on Upstox will be blocked until connectivity is restored.
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={load}
          disabled={loading}
          data-testid="upstox-connectivity-banner-refresh"
          title="Re-check connectivity"
          className="border px-2 py-1 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
          style={{ borderColor: style.border, color: style.color }}
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          Recheck
        </button>
        <button
          onClick={() => setDismissed(true)}
          data-testid="upstox-connectivity-banner-dismiss"
          title="Dismiss banner (will reappear if issue persists)"
          className="text-muted-3 hover:text-text-primary p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};
